import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import type { SearchableComboboxItem } from '@/components/ui/searchable-combobox';

import { DocFieldsLine } from './DocFieldsLine';
import { Hint } from './Hint';
import type { DraftSheet } from './SheetSplitEditor';
import { SourceMark } from './SourceMark';
import { RemarkField, RemarkLine } from './SplitRemark';
import { TypeCombobox } from './TypeCombobox';
import { typeMatches } from './typeSearch';
import { NO_TYPE_VALUE, withNoTypeOption } from './useDocTypeOptions';

const LEAVE_OUT_REASONS = [
    'Empty',
    'Scratch working sheet',
    'Cover page of another sheet',
    'Old copy',
];

const UNSURE_HINT =
    'Claude marked this sheet as unsure. Check it against the sheet.';

export type SheetOrigin = 'Claude' | 'Pipeline' | 'Person';

/** Who made a sheet's answer: 'pipeline' and 'claude' are the stored drafters, null is a person. */
export function sheetOrigin(draftedBy: string | null): SheetOrigin {
    if (draftedBy === null) return 'Person';
    return draftedBy === 'pipeline' ? 'Pipeline' : 'Claude';
}

/** A sheet's own answer in words: its document type, or why it is left out. */
function subLine(row: DraftSheet, typeText: string): string {
    if (!row.answered) return 'No answer yet';
    if (row.include) return typeText || 'No type yet';
    return `Left out · ${row.excludeReason.trim() || 'no reason given'}`;
}

export interface SheetSplitRowProps {
    row: DraftSheet;
    /** 1-based sheet number, shown on the swatch. */
    number: number;
    /** The sheet's colour, shared with its tab under the preview. */
    color: string;
    isSelected: boolean;
    isExpanded: boolean;
    onToggle: () => void;
    /** The document type as shown to people, e.g. "Status Report (SR)". */
    typeText: string;
    docTypes: SearchableComboboxItem[];
    folderOptions: SearchableComboboxItem[];
    foldersLoading: boolean;
    folderEmptyMessage?: string;
    ingestedFileId: number;
    /** Id of the saved document this sheet already makes, if any. */
    matchedDocId: number | null;
    /** Fields filled and asked for on the matched document; 0 and 0 when its type has none. */
    fieldsFilled?: number;
    fieldsTotal?: number;
    /** Changes what the sheet is; clears its Verified tick. */
    onAnswer: (patch: Partial<Pick<DraftSheet, 'include' | 'docType'>>) => void;
    /** Changes a detail that leaves the Verified tick alone. */
    onEdit: (patch: Partial<DraftSheet>, field: string) => void;
    onCheckedChange: (checked: boolean) => void;
    onCreateDocType: (name: string) => void;
    /** Ends a run of typing, so the next keystroke starts a new undo step. */
    onFieldBlur: () => void;
}

const fieldCls = 'h-9 px-2 text-sm';

/** One worksheet as a card: a summary line that expands in place into its answer. */
export function SheetSplitRow({
    row,
    number,
    color,
    isSelected,
    isExpanded,
    onToggle,
    typeText,
    docTypes,
    folderOptions,
    foldersLoading,
    folderEmptyMessage,
    ingestedFileId,
    matchedDocId,
    fieldsFilled = 0,
    fieldsTotal = 0,
    onAnswer,
    onEdit,
    onCheckedChange,
    onCreateDocType,
    onFieldBlur,
}: SheetSplitRowProps) {
    const origin = sheetOrigin(row.draftedBy);
    const claudeUnsure =
        row.answered && row.unsure && origin === 'Claude' && !row.checked;
    const includedChoice = row.answered && row.include;
    const leftOutChoice = row.answered && !row.include;

    return (
        <li
            className={cn(
                'rounded-xl border bg-white',
                isExpanded ? 'border-[#CBE1E1]' : 'border-[#E2F0F0]',
                isSelected && 'bg-[#FBFEFE]',
            )}
            style={
                isSelected ? { boxShadow: `inset 3px 0 0 ${color}` } : undefined
            }
            data-testid="SheetSplitEditor__row"
        >
            <div className="flex items-center gap-3 px-3 py-2">
                <span
                    aria-hidden="true"
                    className={cn(
                        'flex h-7 min-w-[18px] shrink-0 items-center justify-center rounded text-[11px] font-semibold',
                        includedChoice
                            ? 'text-white'
                            : 'border border-dashed border-[#94B8B8] bg-[#F1F5F5] text-slate-500',
                    )}
                    style={
                        includedChoice ? { backgroundColor: color } : undefined
                    }
                    data-testid="SheetSplitEditor__swatch"
                >
                    {number}
                </span>
                <button
                    type="button"
                    className="flex min-h-8 min-w-0 flex-1 flex-col items-start justify-center gap-1 text-left"
                    aria-expanded={isExpanded}
                    aria-label={`Sheet ${number}, ${row.sheetTitle}`}
                    onClick={onToggle}
                    data-testid="SheetSplitEditor__rowToggle"
                >
                    <span className="w-full truncate text-sm font-medium">
                        {row.sheetTitle}
                    </span>
                    <span
                        className={cn(
                            'w-full truncate text-[13px]',
                            !row.answered
                                ? 'italic text-amber-700'
                                : leftOutChoice
                                  ? 'italic text-slate-500'
                                  : 'text-slate-600',
                        )}
                        data-testid="SheetSplitEditor__rowSub"
                    >
                        {subLine(row, typeText)}
                    </span>
                </button>
                {claudeUnsure && (
                    <Hint text={UNSURE_HINT} className="inline-flex shrink-0">
                        <span
                            className="cursor-help whitespace-nowrap text-xs font-medium text-amber-700 underline decoration-dotted underline-offset-2"
                            data-testid="SheetSplitEditor__unsure"
                        >
                            Claude unsure
                        </span>
                    </Hint>
                )}
                {row.answered && (
                    <SourceMark
                        origin={origin}
                        checked={row.checked}
                        editedBy={row.editedBy}
                        checkedBy={row.checkedBy}
                        testId="SheetSplitEditor__sourceMark"
                    />
                )}
                <Hint
                    text="Changing a sheet's document or type clears its Verified tick."
                    className="inline-flex"
                >
                    <Checkbox
                        checked={row.checked}
                        disabled={!row.answered}
                        onCheckedChange={(value) =>
                            onCheckedChange(value === true)
                        }
                        aria-label={`Verified, sheet ${number}`}
                        data-testid="SheetSplitEditor__rowVerified"
                    />
                </Hint>
            </div>

            {row.answered && row.include && (
                <DocFieldsLine
                    ingestedFileId={ingestedFileId}
                    docId={matchedDocId}
                    stale={false}
                    filled={fieldsFilled}
                    total={fieldsTotal}
                    testId="SheetSplitEditor__fieldsLine"
                />
            )}

            {!isExpanded && row.remark.trim() !== '' && (
                <RemarkLine
                    remark={row.remark.trim()}
                    onOpen={onToggle}
                    testId="SheetSplitEditor__remarkLine"
                />
            )}

            {isExpanded && (
                <div
                    className="flex flex-col gap-3 px-3 pb-3"
                    data-testid="SheetSplitEditor__rowEditor"
                >
                    <div
                        role="radiogroup"
                        aria-label="What is this sheet"
                        className="flex self-start overflow-hidden rounded-lg border border-[#CBE1E1] text-[13px]"
                    >
                        {[
                            { label: 'Its own document', value: true },
                            { label: 'Leave out', value: false },
                        ].map((option) => {
                            const on =
                                row.answered && row.include === option.value;
                            return (
                                <button
                                    key={option.label}
                                    type="button"
                                    role="radio"
                                    aria-checked={on}
                                    className={cn(
                                        'px-3 py-2',
                                        on
                                            ? 'bg-[#EFF9F9] font-medium text-slate-900'
                                            : 'bg-white text-slate-600',
                                    )}
                                    onClick={() =>
                                        onAnswer({ include: option.value })
                                    }
                                    data-testid={
                                        option.value
                                            ? 'SheetSplitEditor__setDocument'
                                            : 'SheetSplitEditor__setLeaveOut'
                                    }
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>

                    {includedChoice && (
                        <>
                            <div className="flex flex-col gap-1 text-xs text-slate-600">
                                <span>Document type</span>
                                <TypeCombobox
                                    items={withNoTypeOption(
                                        docTypes,
                                        !!row.docType,
                                    )}
                                    selectedValue={row.docType}
                                    onSelect={(v) =>
                                        onAnswer({
                                            docType:
                                                v === NO_TYPE_VALUE ? '' : v,
                                        })
                                    }
                                    allowClear={false}
                                    filter={(label, search) =>
                                        typeMatches(search, label) ? 1 : 0
                                    }
                                    onCreate={onCreateDocType}
                                    placeholder="Select type"
                                    searchPlaceholder="Search doc types…"
                                    testId={`SheetSplitEditor__docType-${row.key}`}
                                    showValueWhenUnmatched
                                />
                            </div>
                            <div className="flex flex-col gap-1 text-xs text-slate-600">
                                <span>Folder</span>
                                <SearchableCombobox
                                    items={folderOptions}
                                    isLoading={foldersLoading}
                                    emptyMessage={folderEmptyMessage}
                                    selectedValue={row.folder}
                                    onSelect={(v) =>
                                        onEdit({ folder: v }, 'folder')
                                    }
                                    placeholder="Select folder"
                                    searchPlaceholder="Search by code or name…"
                                    testId={`SheetSplitEditor__folder-${row.key}`}
                                    showValueWhenUnmatched
                                />
                            </div>
                        </>
                    )}

                    {leftOutChoice && (
                        <div className="flex flex-col gap-2 text-xs text-slate-600">
                            <label className="flex flex-col gap-1">
                                Why leave it out
                                <Input
                                    value={row.excludeReason}
                                    className={fieldCls}
                                    placeholder="e.g. empty, scratch working sheet, cover page"
                                    onChange={(e) =>
                                        onEdit(
                                            { excludeReason: e.target.value },
                                            'excludeReason',
                                        )
                                    }
                                    onBlur={onFieldBlur}
                                    data-testid="SheetSplitEditor__reason"
                                />
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {LEAVE_OUT_REASONS.map((reason) => (
                                    <Button
                                        key={reason}
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-8 text-xs"
                                        onClick={() =>
                                            onEdit(
                                                { excludeReason: reason },
                                                'excludeReason',
                                            )
                                        }
                                    >
                                        {reason}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}

                    {claudeUnsure && row.notes.trim() !== '' && (
                        <div
                            className="flex flex-col gap-1 rounded-lg bg-[#F4FCFC] px-3 py-2 text-xs text-slate-600"
                            data-testid="SheetSplitEditor__claudeNote"
                        >
                            <span className="font-semibold">
                                Claude's note{' '}
                                <span className="font-normal text-slate-500">
                                    (read only)
                                </span>
                            </span>
                            <span className="text-slate-900">{row.notes}</span>
                        </div>
                    )}

                    <RemarkField
                        id={`remark-${row.key}`}
                        value={row.remark}
                        noun="sheet"
                        onChange={(remark) => onEdit({ remark }, 'remark')}
                        onBlur={onFieldBlur}
                        testId="SheetSplitEditor__remark"
                    />

                    {row.answered && row.include && (
                        <div
                            className="text-xs text-slate-600"
                            data-testid="SheetSplitEditor__who"
                        >
                            {matchedDocId !== null
                                ? 'Created'
                                : 'Not created yet'}
                        </div>
                    )}
                </div>
            )}
        </li>
    );
}
